// Package imports:
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import Bugsnag from '@bugsnag/js';
// Component imports:
import Button from '../../ui-elements/Button/Button';
import Link from '../../ui-elements/Link/Link';
import Alert from '../../ui-elements/Alert/Alert';
import Table from '../../ui-elements/Table/Table';
import Loading from '../../ui-elements/Loading/Loading';
import DisplayBox from '../DisplayBox/DisplayBox';
import HistoryButtonDropDown from '../HistoryButtonDropDown/HistoryButtonDropDown';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import SmallSearch from '../SmallSearch/SmallSearch';
// Service imports:
import { getEventUrl } from '../../services/utils';
import { GET_KELDAN_API_URL } from '../../services/config';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { MyReportsSearchViewModel } from '../../types/MyKeldaTypes';
import { OwnedReport } from '../../types/KeldanTypes';


const ReportsPage: React.FC = () => {
   
    const [ searchResultTerm, setSearchResultTerm] = useState<string>('');
	const [ searchResults, setSearchResults ] = useState<OwnedReport[] | null>();

	const handleSubmit = async (term: string) => {
        setSearchResults(null);
        try {
			const requestQuery = new URLSearchParams({
				term: term,
			});
			const url = `${GET_KELDAN_API_URL()}/MyKelda/ReportsSearch`;
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
				},
				body: requestQuery
			});
			if (response.ok) {
				if (response.redirected) {
					window.location.href = response.url;
				} else {
					const responseBody: MyReportsSearchViewModel = await response.json();
					setSearchResults(responseBody.result);
                }
			} else {
				throw new Error(ErrorMessages.RequestFailed)
			}
		} catch (e) {
			if (e instanceof Error) Bugsnag.notify(e);
        }
    }

	const displaySearchResults = () => {
		return (
		<div>
			{
			// searchResults
			(searchResults !== undefined)
				//show loading
			? (searchResults !== null)
				?(searchResults.length !== 0)
					? <div className='searchResults_container' >
						<div className='searchResults_wrapper'>
							<div className='searchResult-title-Wrapper'>
								<h3>Leitarniðurstöður fyrir {searchResultTerm}</h3>
								<FontAwesomeIcon onClick={() => setSearchResults(undefined)} icon={faXmark} className="icon-fa"/>
							</div>
							<DisplayBox>
								<Table 
									tableSize='lg'
									data={searchResults} 
									columns={[
										{
											title: 'Dags. pöntunar',
											dataAccessor: ({date}) => date.replace(/\//g, '.'),
											textAlign: 'left',
											simpleSortable: ({date}) => date,
										}, {
											title: 'Tegund',
											dataAccessor: ({typeName}) => typeName,
											textAlign: 'left',
											simpleSortable: ({typeName}) => typeName,
										}, {
											title: 'Ár',
											dataAccessor: ({yearValid}) => yearValid,
											textAlign: 'left',
											simpleSortable: ({yearValid}) => yearValid,
										}, {
											title: 'Tilvísun',
											dataAccessor: ({reference}) => reference,
											textAlign: 'left',
											simpleSortable: ({reference}) => reference,
										}, {
											title: 'Leitarorð',
											dataAccessor: ({value}) => value,
											textAlign: 'left',
											simpleSortable: ({value}) => value,
										}, {
											title: '',
											dataAccessor: ({value, typeName, balls, fileId}) => <Button anchorProps={{href: `${getEventUrl(value, typeName, balls, fileId)}`}} size='sm' buttonType='secondary'>Skoða</Button>,
										}, 
									]}
								/>
							</DisplayBox>
						</div>
					</div>
					: <div className='searchResults_container'>
						<div className='searchResults_wrapper'>
							<div className='searchResult-title-Wrapper'>
								<h3>Leitarniðurstöður fyrir {searchResultTerm}</h3>
								<FontAwesomeIcon onClick={() => setSearchResults(undefined)} icon={faXmark} className="icon-fa"/>
							</div>
							<DisplayBox>
								<Alert type="info" headText={'Engar niðurstöður fundust fyrir ' + searchResultTerm } />
							</DisplayBox>
						</div>
					 </div>
				: <div className='searchResults_container'>
					<div className='searchResults_wrapper'>
						<div className='searchResult-title-Wrapper'>
							<h3>Leitarniðurstöður fyrir {searchResultTerm}</h3>
							<FontAwesomeIcon onClick={() => setSearchResults(undefined)} icon={faXmark} className="icon-fa"/>
						</div>
						<DisplayBox>
							<Loading />
						</DisplayBox>
					</div>
				</div>
			: null
			}
			</div>)
	}

	return (
		<div className="main KCL_reportsPage">
			<div className="section section_upper">
				<div className='section_upper-body'>
					<div className='shell'>
						<div className='row'>
							<div className='col-sm-12'>
								<HeadSection
									title={
										<>
											<h1>Keyptar skýrslur</h1>
											<HistoryButtonDropDown />
										</>
									}
								/>
							</div>
						</div>
						<SmallSearch
							search={searchResultTerm}
							setSearch={setSearchResultTerm}
							onSubmit={handleSubmit}
							placeHolder="Leitaðu eftir tegund skýrslu, upprunalegum leitarorðum, tilvísun reiknings og fleira..."
						/>
					</div>
					{displaySearchResults()}
				</div>
			</div>

			<div className="section section_lower">
				<div className='section__lower-body'>
					<div className='shell'>
						<div className='row list-title'>
							<h2>Flokkar</h2>
						</div>
						<div className='list_wrapper'>
								<ul>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Fyrirtaeki'>Fyrirtæki</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Hlutafelagathatttaka'>Hlutafélagaþátttaka</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Samanburdur'>Samanburður</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Fasteignir'>Fasteignir</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Listar'>Listar</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Vinnuvelar'>Vinnuvélar</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Fyrirmyndarfyrirtaeki'>Fyrirmyndarfyrirtæki</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Logbirtingar'>Lögbirtingar</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Thjodskra'>Þjóðskrá</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Hlutafelog'>Hlutafélög</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Reikningar'>Reikningar</Link>
									</li>
									<li>
										<Link linkSize='18' href='/MyKelda/Reports/Okutaeki'>Ökutæki</Link>
									</li>
								</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}



export default ReportsPage;